import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import SEO from '../components/SEO';

const PROJECTS = [
  {
    id: 'whitlam-library',
    title: 'Whitlam Library Upgrade',
    client: 'Western Sydney University',
    location: 'Victoria Road, Rydalmere, NSW',
    council: 'City of Parramatta',
    architect: 'Kosloff Architecture / ADP Consulting',
    type: 'Green Star Interiors v1.3 — Credit 7A Specialist Plan',
    year: '2026',
    color: 'blue',
    description: 'PlanGen Consulting was engaged to provide waste management consultancy and advisory services for the fitout upgrade of the Whitlam Library at Western Sydney University\'s Parramatta South campus. The project targeted a 5-star Green Star Interiors v1.3 rating, requiring a specialist Operational Waste Management Plan compliant with Credit 7A.',
    scope: [
      'Review of concept drawings and liaison with Kosloff Architecture and ADP',
      'Identification and resolution of potential issues relating to City of Parramatta waste management standards',
      'Calculation of projected fitout and operational waste volumes',
      'Recommendation of internal tenancy waste systems and equipment',
      'Development of waste movement pathway markups',
      'Preparation of Green Star Operational WMP for Credit 7A submission',
      'Liaison with traffic engineers regarding collection vehicle access',
    ],
    deliverables: [
      'Green Star Interiors v1.3 Credit 7A Operational Waste Management Plan',
      'Fitout construction waste volume calculations',
      'Operational waste volume calculations',
      'Waste storage area design recommendations',
      'Waste contractor and collection vehicle specifications',
    ],
    outcome: 'The Operational WMP was prepared to satisfy the Green Star Interiors v1.3 Credit 7A Specialist Plan requirement, supporting the project\'s target of a 5-star Green Star rating. The plan addressed both fitout-phase and operational-phase waste management, incorporating City of Parramatta requirements and GBCA submission standards.',
    icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  },
  {
    id: 'foundation-theatres',
    title: 'Foundation Theatres Cold Shell Redevelopment',
    client: 'Foundation Theatres / The Star',
    location: 'Pyrmont, Sydney, NSW',
    council: 'City of Sydney',
    architect: 'Altis Architecture',
    type: 'Demolition & Construction WMP',
    year: '2025–2027',
    color: 'amber',
    description: 'PlanGen Consulting is providing waste management planning services for the Foundation Theatres Cold Shell Redevelopment at The Star entertainment precinct in Pyrmont. This major project involves transforming a cold shell space into a 431-seat multi-level theatre complex, with associated foyer, food & beverage, back-of-house, and performance support facilities.',
    scope: [
      'Demolition waste volume estimates for existing cold shell fitout strip-out',
      'Construction waste management strategy for multi-level theatre build',
      'Material recovery and recycling targets for demolition and construction phases',
      'On-site waste segregation and handling procedures',
      'Licensed waste facility identification and disposal logistics',
      'Waste tracking and reporting framework across the 450-day construction program',
    ],
    deliverables: [
      'Demolition & Construction Waste Management Plan (D&CWMP)',
      'Demolition waste volume calculations',
      'Material recovery target documentation (80%+ diversion)',
      'Licensed waste facility register',
      'Construction-phase waste logistics plan',
    ],
    outcome: 'The D&CWMP addresses the full 450-day construction program from demolition through to practical completion, coordinating with Altis Architecture and the broader project team to ensure waste management obligations are met across all phases of this complex theatre redevelopment.',
    icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
];

const OTHER_PROJECTS = [
  { name: 'Canterbury Hospital Redevelopment', type: 'Green Star OpWMP (REF)', year: '2025–2026' },
  { name: 'Homes NSW — HAFF Tranche', type: 'OpWMP & D&CWMP (Multiple Delivery)', year: '2024–2025' },
  { name: 'St George Private Hospital SSDA', type: 'SSDA OpWMP & D&CWMP', year: '2024–2025' },
  { name: '201 Elizabeth St — Charter Hall', type: 'OpWMP & D&CWMP (Green Star)', year: '2024' },
  { name: 'Oran Park Film Studios', type: 'OpWMP & D&CWMP (Multiple Delivery)', year: '2024' },
  { name: 'IQ3 Westmead SSDA', type: 'SSDA OpWMP & D&CWMP', year: '2024' },
  { name: 'Warakirri College', type: 'OpWMP & D&CWMP (DA)', year: '2024' },
  { name: 'Midtown Primary School (SINSW)', type: 'OpWMP & D&CWMP (DA & s4.55)', year: '2023' },
  { name: 'Uniting Care — Shellharbour', type: 'OpWMP & D&CWMP (DA & s4.55)', year: '2023' },
];

export default function Projects() {
  return (
    <PublicLayout>
      <SEO
        title="Projects — Waste Management Plan Case Studies"
        description="Selected waste management plan projects including the Whitlam Library (Green Star) and Foundation Theatres at The Star, Pyrmont. See our work across hospitals, universities, theatres, and residential developments."
        keywords="waste management plan case study, Green Star WMP project, demolition waste management plan example, WMP consultant portfolio, waste management plan Sydney, WMP Parramatta, WMP Pyrmont"
        canonicalPath="/projects"
      />

      {/* Hero */}
      <section className="py-16 lg:py-20 bg-[#F8FAFC]" data-testid="projects-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">OUR PROJECTS</div>
            <h1 className="text-3xl lg:text-4xl font-black tracking-tight text-[#0A2540] leading-tight mb-4" data-testid="projects-headline">
              Selected Projects
            </h1>
            <p className="text-base text-slate-600 leading-relaxed">
              We have delivered waste management plans for a diverse range of developments — from university campuses and hospitals to theatre complexes and government housing. Below is a selection of recent work.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Case Studies */}
      <section className="py-16 bg-white" data-testid="case-studies">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-20">
          {PROJECTS.map((project) => {
            const isBlue = project.color === 'blue';
            const accentColor = isBlue ? '#3b82f6' : '#f59e0b';
            const bgGradient = isBlue ? 'from-blue-50 to-indigo-50' : 'from-amber-50 to-orange-50';
            const tagBg = isBlue ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700';
            const iconBg = isBlue ? 'bg-blue-100' : 'bg-amber-100';
            const iconColor = isBlue ? 'text-blue-600' : 'text-amber-600';

            return (
              <div key={project.id} id={project.id} data-testid={`project-${project.id}`}>
                {/* Header card */}
                <div className={`bg-gradient-to-br ${bgGradient} rounded-2xl p-8 lg:p-10 mb-8`}>
                  <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-start">
                    <div>
                      <div className="flex flex-wrap gap-2 mb-4">
                        <span className={`text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${tagBg}`}>{project.type}</span>
                        <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-white/80 text-slate-600">{project.council}</span>
                        <span className="text-[10px] font-semibold px-2.5 py-0.5 rounded-full bg-white/80 text-slate-600">{project.year}</span>
                      </div>
                      <h2 className="text-2xl lg:text-3xl font-bold text-[#0A2540] mb-2">{project.title}</h2>
                      <p className="text-sm text-slate-600">{project.client} &mdash; {project.location}</p>
                      {project.architect && <p className="text-sm text-slate-500 mt-1">Architect: {project.architect}</p>}
                    </div>
                    <div className={`w-16 h-16 rounded-2xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
                      <svg className={`w-8 h-8 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={project.icon} />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-8">
                  <div>
                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">Project Overview</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-6">{project.description}</p>

                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">Scope of Work</h3>
                    <ul className="space-y-2 mb-6">
                      {project.scope.map((s, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                          <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {s}
                        </li>
                      ))}
                    </ul>

                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">Outcome</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{project.outcome}</p>
                  </div>

                  <div>
                    <div className="bg-slate-50 rounded-2xl border border-slate-100 p-6 sticky top-28">
                      <h3 className="text-sm font-bold text-[#0A2540] mb-4">Key Deliverables</h3>
                      <ul className="space-y-2.5">
                        {project.deliverables.map((d, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: accentColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {d}
                          </li>
                        ))}
                      </ul>
                      <div className="mt-6 pt-4 border-t border-slate-200">
                        <div className="grid grid-cols-2 gap-3 text-center">
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase font-bold">Client</p>
                            <p className="text-xs text-[#0A2540] font-medium mt-0.5">{project.client}</p>
                          </div>
                          <div>
                            <p className="text-[10px] text-slate-400 uppercase font-bold">Council</p>
                            <p className="text-xs text-[#0A2540] font-medium mt-0.5">{project.council}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Other Projects */}
      <section className="py-16 bg-[#F8FAFC]" data-testid="other-projects">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">PROJECT HISTORY</div>
            <h2 className="text-2xl lg:text-3xl font-bold tracking-tight text-[#0A2540]">Other Selected Projects</h2>
            <p className="text-sm text-slate-500 mt-3 max-w-lg mx-auto">A selection of additional projects delivered across NSW and VIC. Some client details remain confidential.</p>
          </div>
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              {OTHER_PROJECTS.map((p, i) => (
                <div key={i} className={`flex items-center justify-between px-6 py-4 ${i < OTHER_PROJECTS.length - 1 ? 'border-b border-slate-50' : ''}`} data-testid={`other-project-${i}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#0A2540]">{p.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.type}</p>
                  </div>
                  <span className="text-xs text-slate-400 font-medium flex-shrink-0 ml-4">{p.year}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0A2540]" data-testid="projects-cta">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 tracking-tight">Have a similar project?</h2>
          <p className="text-slate-400 text-base mb-8 max-w-lg mx-auto">Whether it&apos;s a DA submission, Green Star credit, or SSDA &mdash; send us your plans and we&apos;ll provide a tailored quote.</p>
          <Link to="/contact" className="inline-flex px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg" data-testid="projects-cta-btn">
            Request a Quote
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
